import { useEffect, useState } from 'react';
import { Configuration, UsersApi } from '@/api';
import { UserSort } from '@/api/UsersApi';
import { EmptyResult } from '@/models/Pagination';
import { User, Users } from '@/models/User';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { setPaginationResultIfNotLoading } from '@/utils/pagination';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState = EmptyResult<User>();

interface FindUsersQueryResult {
  users: Users;
  isLoading: boolean;
  error: Error | null;
  itemsPerPage: number;
  invalidateQuery: () => Promise<void>;
}

const useFindUsersQuery = (
  page: number,
  searchQuery?: string,
  sort?: UserSort[],
): FindUsersQueryResult => {
  const queryClient = useQueryClient();
  const auth = useAuth();

  const [users, setUsers] = useState<Users>(initialState);

  const usersApi = new UsersApi(new Configuration(auth));

  const itemsPerPage = 10;

  const { isLoading, data, isFetching, error } = useQuery({
    queryKey: ['users', page, searchQuery, sort],
    queryFn: () => {
      return usersApi.findUsers(
        searchQuery,
        sort,
        page * itemsPerPage - itemsPerPage,
        itemsPerPage,
      );
    },
  });

  useEffect(() => {
    setPaginationResultIfNotLoading(data, setUsers, isLoading);
  }, [data, isLoading, isFetching]);

  const invalidateQuery = async () => {
    return queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === 'users',
    });
  };

  return {
    users,
    isLoading,
    error,
    itemsPerPage,
    invalidateQuery,
  };
};

export default useFindUsersQuery;
